import React, { useRef, useEffect, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let _mapsPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps?.Map) return Promise.resolve();
  if (_mapsPromise) return _mapsPromise;
  _mapsPromise = new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) { clearInterval(check); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(check); reject(new Error("timeout")); }, 10000);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=geocoding`;
    script.async = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) { clearInterval(check); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(check); reject(new Error("timeout")); }, 10000);
    };
    script.onerror = () => reject(new Error("failed"));
    document.head.appendChild(script);
  });
  return _mapsPromise;
}


export default function DealMapCard({ address, lat, lng, size = "sm", className = "" }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | failed | no_key

  const initMap = useCallback(async (latitude, longitude) => {
    if (!mapRef.current || mapInstanceRef.current) return;
    try {
      await loadGoogleMaps();
      const pos = { lat: latitude, lng: longitude };
      const map = new window.google.maps.Map(mapRef.current, {
        center: pos,
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: "none",
        styles: [
          { featureType: "all", elementType: "geometry", stylers: [{ saturation: -30 }] },
          { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#c8e6d8" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ lightness: 10 }] },
        ],
      });
      new window.google.maps.Marker({ position: pos, map, icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#022c22",
        fillOpacity: 1,
        strokeColor: "#e5c98b",
        strokeWeight: 2,
      }});
      mapInstanceRef.current = map;
      setStatus("ready");
    } catch {
      setStatus("failed");
    }
  }, []);

  useEffect(() => {
    if (!GOOGLE_KEY) { setStatus("no_key"); return; }

    // Set a timeout — if map doesn't load within 8s, show fallback
    const timeout = setTimeout(() => {
      if (status === "loading") setStatus("failed");
    }, 8000);

    if (lat && lng) {
      initMap(lat, lng);
      return () => clearTimeout(timeout);
    }
    // Geocode the address using the Maps JavaScript API geocoder
    if (address) {
      loadGoogleMaps().then(() => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, geoStatus) => {
          if (geoStatus === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            initMap(loc.lat(), loc.lng());
          } else {
            setStatus("failed");
          }
        });
      }).catch(() => setStatus("failed"));
    } else {
      setStatus("failed");
    }

    return () => clearTimeout(timeout);
  }, [address, lat, lng, initMap]);

  const dims = size === "lg" ? "h-48" : size === "md" ? "h-36" : "h-24";

  if (status === "no_key" || status === "failed") {
    return (
      <div className={`${dims} rounded-lg bg-emerald-950/30 border border-emerald-800/20 flex items-center justify-center ${className}`} data-testid="deal-map-fallback">
        <div className="text-center px-2">
          <MapPin className="h-4 w-4 text-emerald-600/40 mx-auto mb-1" />
          <p className="text-[10px] text-emerald-600/40 leading-tight truncate max-w-[180px]">{address || "No address"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${dims} rounded-lg overflow-hidden border border-emerald-800/20 ${className}`} data-testid="deal-map-card">
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-emerald-950/30 z-10">
          <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}
